import React from 'react';

import {
  EyewearCard,
  WatchCard,
} from '@/components/ProductListingCard/components';
import { useSendClickedAlgoliaEvent } from '@/hooks/useSendAlgoliaEvent';
import { isEyewearHit } from '@/integrations/algolia/helpers';
import type { EyewearHit, WatchHit } from '@/integrations/algolia/types';
import type { ProductCatalogModeCtaStyle } from '@/lib/types';

import type { EndImageProps } from './EndImage';
import { EndImage } from './EndImage';

import * as S from './ScrollableRow.styles';

export interface ProductReferencesProps {
  __typename: string | null | undefined;
  manualProductReferences?: string[];
}

export interface ScrollableRowProps {
  endImage: EndImageProps;
  hits: WatchHit[] | EyewearHit[];
  productCatalogModeCtaStyle?: ProductCatalogModeCtaStyle;
  productReferences: ProductReferencesProps;
  products: WatchHit[];
  startIndex: number;
}

const ScrollableRow: React.FC<ScrollableRowProps> = ({
  endImage,
  hits,
  productCatalogModeCtaStyle,
  startIndex,
}) => {
  const sendClickedAlgoliaEvent = useSendClickedAlgoliaEvent();

  const renderProductCard = (
    hit: WatchHit | EyewearHit | undefined,
    index: number,
  ) => {
    if (!hit) {
      return null;
    }

    if (isEyewearHit(hit)) {
      return (
        <EyewearCard
          key={hit.objectID}
          cardClickHandler={() => sendClickedAlgoliaEvent(hit.objectID)}
          data-test-id={`ScrollableRowWatchCard-${index}`}
          hit={hit}
          productCatalogModeCtaStyle={productCatalogModeCtaStyle}
          sizes='(max-width: 600px) 50vw, 18vw'
        />
      );
    }

    return (
      <WatchCard
        key={hit.objectID}
        cardClickHandler={() => sendClickedAlgoliaEvent(hit.objectID)}
        data-test-id={`ScrollableRowWatchCard-${index}`}
        hit={hit}
        productCatalogModeCtaStyle={productCatalogModeCtaStyle}
        sizes='(max-width: 600px) 50vw, 18vw'
      />
    );
  };

  return (
    <S.ScrollableRow data-testid='ScrollableRow'>
      {hits
        ?.slice(startIndex)
        .map((reference, index) => renderProductCard(reference, index))}
      {endImage && (
        <EndImage data-test-id='ScrollableRowEndImage' endImage={endImage} />
      )}
    </S.ScrollableRow>
  );
};

export default ScrollableRow;
